/* eslint-disable */
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

const APP_INFO_TIMEOUT_MS = 5000;

const detectOs = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/android/.test(userAgent)) return 'android';
  if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
  return 'ios';
};

const getFallbackAppInfo = () => ({
  os: detectOs(),
  appName: '야옹상회',
  packageName: 'com.yaong.app',
  version: '0',
  buildVersion: '0',
});

const waitForFlutterBridge = () =>
  new Promise((resolve) => {
    if (typeof window.flutter_inappwebview?.callHandler === 'function') {
      resolve(true);
      return;
    }

    let settled = false;
    const finish = (isReady) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('flutterInAppWebViewPlatformReady', onReady);
      window.clearTimeout(timeoutId);
      resolve(isReady);
    };
    const onReady = () => finish(typeof window.flutter_inappwebview?.callHandler === 'function');
    const timeoutId = window.setTimeout(() => finish(false), APP_INFO_TIMEOUT_MS);

    window.addEventListener('flutterInAppWebViewPlatformReady', onReady, { once: true });
  });

const fetchAppInfo = async () => {
  const isFlutterReady = await waitForFlutterBridge();
  if (!isFlutterReady) {
    return getFallbackAppInfo();
  }

  try {
    const info = await Promise.race([
      window.flutter_inappwebview.callHandler('getAppInfo'),
      new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error('getAppInfo timeout')), APP_INFO_TIMEOUT_MS);
      }),
    ]);

    if (info && typeof info === 'object') {
      return info;
    }
  } catch (error) {
    console.error('getAppInfo failed:', error);
  }

  return getFallbackAppInfo();
};

export const AuthContext = createContext({});

export const AuthContextProvider = ({ children }) => {
  const isDevMode = String(process.env.REACT_APP_DEV).trim().toLowerCase() === 'true';
  const devApi = String(process.env.REACT_APP_DEV_API);
  // const API_SERVER_DOMAIN = devApi
  //   ? devApi
  //   : isDevMode
  //     ? 'https://dev-api.yaongstore.com'
  //     : 'https://api.yaongstore.com';
  const API_SERVER_DOMAIN = isDevMode ? 'https://dev-api.yaongstore.com' : 'https://api.yaongstore.com';


  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [appInfo, setAppInfo] = useState(null);
  const [eventListeners, setEventListeners] = useState([]);

  let isLocked = false;

  const fetchWithToken = ([url, token]) =>
    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }).then((res) => res.json());

  const getAccessToken = async () => {
    const now = new Date();
    const token = JSON.parse(localStorage.getItem('accessToken'));

    // 토큰 만료 시간이 10분 이하면 재발급 요청
    // debug: 토큰 6분 후 만료 // 5040000
    if (token && now.getTime() > token.expiry - 600000 && !isLocked) {
      // console.debug("토큰 재발행 시작");
      isLocked = true;
      const response = await fetch(`${API_SERVER_DOMAIN}/auth/token/refresh`, {
        headers: {
          Authorization: `RefreshToken ${localStorage.getItem('refreshToken')}`,
        },
      });
      const jsonData = await response.json();
      if (jsonData.result == 'ok') {
        setAccessToken(jsonData.token.accessToken, jsonData.token.accessTokenExpiresIn, jsonData.token.refreshToken);
        isLocked = false;
        // console.debug("토큰 재발행 완료");
        return jsonData.token.accessToken;
      } else {
        return null;
      }
    }

    // 토큰이 있음
    if (token) {
      // 유효성 검사
      const response = await fetch(`${API_SERVER_DOMAIN}/auth/user/check`, {
        headers: {
          Authorization: `Bearer ${token.value}`,
          'Content-Type': 'application/json',
        },
      });
      const jsonData = await response.json();
      // 유효하지 않음
      if (jsonData.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setToken(null);
        return null;
      }

      return token.value;
    }

    return null;
  };

  const setAccessToken = (accessToken, accessTokenExpiresIn, refreshToken) => {
    const now = new Date();
    const token = {
      value: accessToken,
      expiry: now.getTime() + accessTokenExpiresIn,
    };
    localStorage.setItem('accessToken', JSON.stringify(token));
    localStorage.setItem('refreshToken', refreshToken);
  };

  // 로그인 성공시 리스너 호출
  const notifyLoginSuccess = useCallback(
    (token) => {
      eventListeners.forEach((listener) => {
        if (typeof listener === 'function') {
          listener(token);
        }
      });
    },
    [eventListeners],
  );

  // 콜백 등록 함수 - cleanup 함수 반환
  const addLoginListener = useCallback((callback) => {
    setEventListeners((prevListeners) => [...prevListeners, callback]);

    return () => setEventListeners((prevListeners) => prevListeners.filter((listener) => listener !== callback));
  }, []);

  const signIn = (accessToken, accessTokenExpiresIn, refreshToken) => {
    fetch(`${API_SERVER_DOMAIN}/auth/cert/check`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((obj) => {
        if (obj.result === 'ok' && obj.cert_state === 'Y') {
          setAccessToken(accessToken, accessTokenExpiresIn, refreshToken);
          setToken(accessToken);
          // 로그인 성공 이벤트 발생
          notifyLoginSuccess({ accessToken });
          navigate('/playground');
        } else {
          navigate('/verification', {
            state: { accessToken: accessToken, accessTokenExpiresIn: accessTokenExpiresIn, refreshToken: refreshToken },
          });
        }
      });
  };

  // 테스트 로그인. 토큰을 요청함.
  const signInTest = (loginType, token) => {
    fetch(`${API_SERVER_DOMAIN}/auth/login`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        login_type: loginType,
        token: token,
      }),
    })
      .then((response) => response.json())
      .then((obj) => {
        if (obj.result == 'ok') {
          signIn(obj.token.accessToken, obj.token.accessTokenExpiresIn, obj.token.refreshToken);
        } else {
          console.error('Login error.');
        }
      });
  };

  const signOut = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    setToken(null);
    navigate('/login');
  };

  const useAccessToken = () => {
    /* (async () => {
      const accessToken = await getAccessToken();
      setToken(accessToken);
    })(); */

    return token;
  };

  const isPublicAuthPath = (pathname) =>
    pathname === '/login' || pathname === '/download' || pathname === '/verification' || pathname === '/oauth';

  useEffect(() => {
    getAccessToken().then((accessToken) => {
      if (!accessToken && !isPublicAuthPath(location.pathname)) {
        navigate('/login', { replace: true });
      } else {
        setToken(accessToken);
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    /**
     * 앱 정보 가져오는 방식입니다.
     *
     * appName: 야옹상회 {String}
     * packageName: com.yaong.app {String}
     * version: 240529 {String}
     * buildVersion: 4 {String}
     *
     * 요런식으로 들어옵니다. 참고해주세요!
     */
    fetchAppInfo().then((info) => {
      if (!cancelled) {
        setAppInfo(info);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        API_SERVER_DOMAIN,
        useAccessToken,
        fetchWithToken,
        signIn,
        signInTest,
        signOut,
        appInfo,
        addLoginListener,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
